import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  Observable,
  split,
  ApolloLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { ENV } from "../utils/configs";
import { cache } from "../utils/cache";
import axios from "axios";
import { from, throwError } from "rxjs";
import { switchMap, catchError } from "rxjs/operators";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { StorageHelper } from "../utils/storageHelper";

/**
 * ApolloService
 * - Handles Apollo Client setup for HTTP and WebSocket GraphQL operations.
 * - Manages authentication token refresh and queues failed operations during refresh.
 * - Handles GraphQL and network errors, including UNAUTHENTICATED errors.
 * - Uses RxJS for async token refresh and retry logic.
 * - Ensures a single Apollo Client instance is used throughout the app.
 * - Supports subscriptions via WebSocket and queries/mutations via HTTP.
 * - Automatically attaches JWT token from storage to every request.
 * - Provides detailed debug logs for token refresh and operation retry flows.
 */

let isRefreshing = false; // Indicates if a token refresh is in progress
let tokenSubscribers = new Map(); // Not used, but can be for advanced flows
let failedQueue = []; // Queue of callbacks for operations waiting for a new token
let failedoperation = []; // List of failed operation keys
let currentToken = StorageHelper.get("token"); // Current JWT token
let pendingOperations = new Map(); // Not used, but can be for advanced flows
let apolloClient = null; // Singleton Apollo Client instance

/**
 * Generates a unique key for a GraphQL operation based on its name and variables.
 * Used to prevent duplicate retries during token refresh.
 */
const getOperationKey = (operation) => {
  return `${operation.operationName}:${JSON.stringify(operation.variables)}`;
};

/**
 * Requests a new JWT token from the backend using the RefreshToken mutation.
 * Stores the new token in local storage and updates currentToken.
 * If refresh fails, redirects to login.
 */
const refreshToken = async () => {
  try {
    console.log("🔄 [REFRESH] Requesting new token");
    const response = await axios.post(
      ENV.api_url,
      {
        query: `
          mutation RefreshToken {
            RefreshToken {
              access_token
            }
          }
        `,
        operationName: "RefreshToken",
      },
      {
        headers: {
          Authorization: `Bearer ${StorageHelper.get("token")}`,
        },
      }
    );

    const error = response.data?.errors;
    if (error) {
      console.log("❌ [REFRESH] Server returned error", error);
      window.open("/login", "_self");
      throw new Error(error);
    }

    const new_token = response.data?.data?.RefreshToken?.access_token;
    StorageHelper.set("token", new_token);
    currentToken = new_token;
    console.log("✅ [REFRESH] New token stored");

    return new_token;
  } catch (error) {
    console.error("❌ [REFRESH] Token refresh failed:", error);
    throw error;
  }
};

/**
 * ApolloService class encapsulates Apollo Client setup and token refresh logic.
 * Ensures only one instance is created (singleton pattern).
 */
class ApolloService {
  constructor() {
    if (!ApolloService.instance) {
      this.initializeClient();
      ApolloService.instance = this;
    }
    return ApolloService.instance;
  }

  /**
   * Initializes Apollo Client with HTTP, WebSocket, Auth, and Error links.
   * Handles token refresh and operation retry logic.
   */
  initializeClient() {
    // HTTP link for queries and mutations
    const httpLink = createHttpLink({
      uri: ENV.api_url,
    });

    // WebSocket link for subscriptions
    const wsLink = new GraphQLWsLink(
      createClient({
        url: ENV.socket_url,
        // connectionParams: () => ({
        //   authorization: `Bearer ${currentToken}`,
        // }),
      })
    );

    // Split link: routes subscription operations to wsLink, others to httpLink
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      wsLink,
      httpLink
    );

    // Auth link: attaches JWT token and hostname to every request
    const authLink = setContext((_, { headers }) => {
      const token = StorageHelper.get("token");
      currentToken = token;

      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : "",
          hostname: window.location.hostname,
        },
      };
    });

    // Error link: handles UNAUTHENTICATED errors and retries operations after token refresh
    const errorLink = onError(
      ({ graphQLErrors, networkError, operation, forward }) => {
        let handled = false;
        const operationKey = getOperationKey(operation);
        if (graphQLErrors) {
          for (let err of graphQLErrors) {
            if (err?.extensions?.code === "UNAUTHENTICATED") {
              console.log(`[AUTH] UNAUTHENTICATED error for operation: ${operationKey}`);
              if (!isRefreshing) {
                isRefreshing = true;
                console.log(`[AUTH] Starting token refresh. Queued operations:`, failedoperation);
                return new Observable((observer) => {
                  from(refreshToken())
                    .pipe(
                      switchMap((token) => {
                        operation.setContext(({ headers }) => ({
                          headers: {
                            ...headers,
                            authorization: `Bearer ${token}`,
                          },
                        }));
                        return forward(operation);
                      }),
                      catchError((error) => {
                        return throwError(error);
                      })
                    )
                    .subscribe({
                      next: (result) => {
                        isRefreshing = false;
                        console.log(`[AUTH] Token refresh complete. Resolving all queued operations. Queue length: ${failedQueue.length}`);
                        failedQueue.forEach((callback, idx) => {
                          console.log(`[AUTH] Resolving queued operation #${idx + 1}`);
                          callback(result);
                        });
                        failedQueue = [];
                        failedoperation = [];
                        observer.next(result);
                        observer.complete();
                        // After resolving all queued operations, clear the Set
                        console.log('[DEBUG] Clearing retried keys set after all retries');
                        if (window.__apolloRetriedKeys) window.__apolloRetriedKeys.clear();
                      },
                      error: (error) => {
                        isRefreshing = false;
                        console.log(`[AUTH] Token refresh failed. Rejecting all queued operations. Queue length: ${failedQueue.length}`);
                        failedQueue.forEach((callback, idx) => {
                          console.log(`[AUTH] Rejecting queued operation #${idx + 1}`);
                          callback(error);
                        });
                        failedQueue = [];
                        failedoperation = [];
                        observer.error(error);
                        // After failedQueue.forEach(...)
                        console.log('[DEBUG] Clearing retried keys set after all retries');
                        if (window.__apolloRetriedKeys) window.__apolloRetriedKeys.clear();
                      },
                    });
                });
              } else {
                failedoperation.push(operationKey);
                console.log(`[AUTH] Token refresh in progress. Queuing operation: ${operationKey}. Queue length before: ${failedQueue.length}`);
                return new Observable((observer) => {
                  // Track which operation keys have already been retried in this refresh cycle
                  if (!window.__apolloRetriedKeys) window.__apolloRetriedKeys = new Set();
                  console.log(`[DEBUG] Preparing to queue failed operation: ${operationKey}`);
                  console.log(`[DEBUG] Current retried keys:`, Array.from(window.__apolloRetriedKeys));
                  console.log(`[DEBUG] Current failedQueue length: ${failedQueue.length}`);
                  failedQueue.push((result) => {
                    console.log(`[DEBUG] Entered retry callback for: ${operationKey}`);
                    console.log(`[DEBUG] Retried keys before check:`, Array.from(window.__apolloRetriedKeys));
                    if (window.__apolloRetriedKeys.has(operationKey)) {
                      console.log(`[AUTH] Skipping duplicate retry for: ${operationKey}`);
                      return;
                    }
                    window.__apolloRetriedKeys.add(operationKey);
                    console.log(`[AUTH] Retrying queued operation: ${operationKey}`);
                    // Clone the operation for each retry to ensure Apollo sends a new network request
                    const retryOperation = operation.clone ? operation.clone() : {
                      ...operation,
                      setContext: operation.setContext,
                      getContext: operation.getContext,
                      query: operation.query,
                      variables: operation.variables,
                      operationName: operation.operationName,
                    };
                    retryOperation.setContext(({ headers }) => ({
                      headers: {
                        ...headers,
                        authorization: `Bearer ${result.token}`,
                      },
                    }));
                    console.log(`[DEBUG] Subscribing observer for: ${operationKey}`, observer);
                    forward(retryOperation).subscribe({
                      next: (data) => {
                        console.log(`[DEBUG] Observer next for: ${operationKey}`, data);
                        observer.next(data);
                      },
                      error: (err) => {
                        console.log(`[DEBUG] Observer error for: ${operationKey}`, err);
                        observer.error(err);
                      },
                      complete: () => {
                        console.log(`[DEBUG] Observer complete for: ${operationKey}`);
                        observer.complete();
                      }
                    });
                  });
                  console.log(`[AUTH] Operation ${operationKey} added to queue. Queue length after: ${failedQueue.length}`);
                });
              }
            } else {
              // Log all other GraphQL errors
              console.error(`[GraphQL error]:`, err, `Operation:`, operationKey);
              handled = true;
            }
          }
        }
        if (networkError) {
          console.error(`[Network error]:`, networkError, `Operation:`, operationKey);
          handled = true;
        }
        if (!handled) {
          return forward(operation);
        }
        return;
      }
    );

    // Compose the Apollo link chain: error -> auth -> split (ws/http)
    const link = ApolloLink.from([errorLink, authLink, splitLink]);

    // Create Apollo Client instance with custom cache
    apolloClient = new ApolloClient({
      link,
      cache,
    });
  }

  /**
   * Returns the singleton Apollo Client instance.
   */
  getClient() {
    return apolloClient;
  }
}

// Create and export a single instance of ApolloService
const apolloService = new ApolloService();
export const client = apolloService.getClient();
